import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bot, Maximize2, MessageSquarePlus, Sparkles, X } from "lucide-react";

import { cn } from "../../utils/tailwindUtils";
import useCopilot from "../../hooks/useCopilot";
import CopilotComposer from "./CopilotComposer";
import CopilotSuggestions from "./CopilotSuggestions";
import CopilotThread from "./CopilotThread";
import { COPILOT_QUICK_PROMPTS } from "./constants";

const COPILOT_ROUTE = "/ai/copilot";

/**
 * App-wide copilot entry point: a floating pill that opens a docked chat panel.
 *
 * Mounted once in AppLayout, so it is available on every authenticated screen.
 * It hides itself on the full-page workspace to avoid two chats side by side.
 * The panel and the page share a session id via sessionStorage (see useCopilot),
 * so "expand" carries the conversation across.
 */
const CopilotLauncher = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    const {
        sessionId,
        messages,
        sending,
        loadingHistory,
        feedbackState,
        send,
        rate,
        openSession,
        startNewChat,
    } = useCopilot();

    const employeeName = sessionStorage.getItem("employee_name") || "";
    const firstName = employeeName.split(" ")[0] || "";
    const isHr =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

    // Close the panel on navigation so it never covers a screen the user just opened.
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Esc closes the panel, matching the rest of the app's overlays.
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open]);

    // Restore the thread the first time the panel is opened in this page view.
    // The session id survives navigation via sessionStorage, so the backend
    // already treats these as one conversation and feeds prior turns to the
    // model — showing an empty panel would contradict that. Silent, because a
    // session that has never been sent yet has no history to fetch.
    const restoredRef = useRef(false);
    useEffect(() => {
        if (!open || restoredRef.current || messages.length) return;
        restoredRef.current = true;
        openSession(sessionId, { silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleNewChat = () => {
        restoredRef.current = true;
        startNewChat();
    };

    // The workspace page is the copilot — no launcher needed there.
    if (location.pathname.startsWith(COPILOT_ROUTE)) return null;

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                title="Ask the AI Copilot"
                className={cn(
                    "fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full",
                    "bg-sidebar px-4 py-2.5 text-sm font-semibold text-white",
                    "shadow-lg transition-all hover:bg-sidebar-primary hover:shadow-xl",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar focus-visible:ring-offset-2 animate-bounce"
                )}
            >
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Ask AI</span>
                {/* <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold tracking-wide">
                    COPILOT
                </span> */}
            </button>
        );
    }

    return (
        <div
            className={cn(
                "fixed z-40 flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl",
                // Full-width sheet on mobile, docked card from sm up.
                "inset-x-3 bottom-3 top-16 sm:inset-x-auto sm:top-auto",
                "sm:bottom-5 sm:right-5 sm:h-[560px] sm:w-[400px]"
            )}
            role="dialog"
            aria-label="AI Copilot"
        >
            {/* Header */}
            <div className="flex items-center justify-between gap-2 bg-sidebar px-3.5 py-3 text-white">
                <div className="flex min-w-0 items-center gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20">
                        <Bot className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">AI Copilot</p>
                        <p className="flex items-center gap-1.5 text-[11px] text-white/80">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                            Connected to your HR data
                        </p>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-0.5">
                    <button
                        type="button"
                        onClick={handleNewChat}
                        title="New chat"
                        className="rounded-md p-1.5 transition-colors hover:bg-white/20"
                    >
                        <MessageSquarePlus className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            navigate(COPILOT_ROUTE);
                        }}
                        title="Open full workspace"
                        className="rounded-md p-1.5 transition-colors hover:bg-white/20"
                    >
                        <Maximize2 className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        title="Close"
                        className="rounded-md p-1.5 transition-colors hover:bg-white/20"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Quick prompts — hidden once a conversation is under way, to give
                the thread the full height. */}
            {!messages.length && (
                <CopilotSuggestions
                    items={COPILOT_QUICK_PROMPTS}
                    onPick={send}
                    disabled={sending}
                    isHr={isHr}
                    compact
                />
            )}

            <CopilotThread
                messages={messages}
                sending={sending}
                loading={loadingHistory}
                userName={firstName}
                feedbackState={feedbackState}
                onRate={rate}
                compact
            />

            <div className="border-t border-border bg-background p-2.5">
                <CopilotComposer
                    onSend={send}
                    disabled={sending}
                    placeholder="Ask the copilot…"
                    compact
                    autoFocus
                />
            </div>
        </div>
    );
};

export default CopilotLauncher;
